(() => {
class RuncamDetailNavigation extends HTMLElement {
  connectedCallback() {
    this.handleClick = this.handleClick.bind(this);
    this.handleEditorChange = this.refresh.bind(this);
    this.handleDocumentReady = this.handleDocumentReady.bind(this);
    this.handleResize = this.updateOffset.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleScrollEnd = this.handleScrollEnd.bind(this);
    this.handleUserScrollIntent = this.handleUserScrollIntent.bind(this);
    this.stickyContainer = this.closest('.shopify-section') || this;
    this.stickyContainer.classList.add('runcam-detail-navigation-wrapper');
    this.addEventListener('click', this.handleClick);
    document.addEventListener('shopify:section:load', this.handleEditorChange);
    document.addEventListener('shopify:section:unload', this.handleEditorChange);
    document.addEventListener('shopify:section:reorder', this.handleEditorChange);
    window.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('wheel', this.handleUserScrollIntent, { passive: true });
    window.addEventListener('touchstart', this.handleUserScrollIntent, { passive: true });
    window.addEventListener('keydown', this.handleUserScrollIntent);
    document.addEventListener('scrollend', this.handleScrollEnd);
    if (document.readyState === 'loading') {
      this.hidden = !this.querySelector('[data-target-id]');
      document.addEventListener('readystatechange', this.handleDocumentReady);
    } else {
      this.cleanupBootstrap();
      this.refresh();
    }
  }

  disconnectedCallback() {
    this.cancelProgrammaticScroll();
    if (this.scrollFrame !== undefined) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = undefined;
    }
    this.cleanupBootstrap();
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('shopify:section:load', this.handleEditorChange);
    document.removeEventListener('shopify:section:unload', this.handleEditorChange);
    document.removeEventListener('shopify:section:reorder', this.handleEditorChange);
    document.removeEventListener('readystatechange', this.handleDocumentReady);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('wheel', this.handleUserScrollIntent);
    window.removeEventListener('touchstart', this.handleUserScrollIntent);
    window.removeEventListener('keydown', this.handleUserScrollIntent);
    document.removeEventListener('scrollend', this.handleScrollEnd);
    this.stickyContainer.classList.remove('runcam-detail-navigation-wrapper');
    this.stickyContainer.style.removeProperty('--runcam-detail-header-offset');
  }

  handleDocumentReady() {
    if (document.readyState === 'loading') return;
    document.removeEventListener('readystatechange', this.handleDocumentReady);
    this.cleanupBootstrap();
    this.refresh();
  }

  cleanupBootstrap() {
    this.runcamDetailNavigationBootstrap?.cleanup();
    delete this.runcamDetailNavigationBootstrap;
  }

  refresh() {
    this.cancelProgrammaticScroll();
    this.sections = Array.from(document.querySelectorAll('[data-runcam-detail-section][data-show-in-nav="true"]'))
      .filter((section) => section.id && section.dataset.navLabel);
    this.renderLinks();
    this.updateOffset();
  }

  renderLinks() {
    const list = this.querySelector('[data-runcam-detail-links]');
    if (!list) return;

    const currentLinks = Array.from(list.querySelectorAll('[data-target-id]'));
    const linksAreCurrent = currentLinks.length === this.sections.length && currentLinks.every((link, index) => {
      const section = this.sections[index];
      return link.dataset.targetId === section.id && link.textContent === section.dataset.navLabel;
    });

    if (!linksAreCurrent) {
      list.replaceChildren(...this.sections.map((section, index) => {
        const link = document.createElement('a');
        link.className = 'runcam-detail-navigation__link';
        link.href = `#${section.id}`;
        link.textContent = section.dataset.navLabel;
        link.dataset.targetId = section.id;
        if (index === 0) {
          link.classList.add('is-active');
          link.setAttribute('aria-current', 'location');
        }
        return link;
      }));
      this.activeId = null;
    }
    this.hidden = this.sections.length === 0;
  }

  updateOffset() {
    const header = document.querySelector('header-component, .header-section, #shopify-section-header');
    const headerHeight = header?.getBoundingClientRect().height || 0;
    const navHeight = this.getBoundingClientRect().height || 0;
    const offset = Math.round(headerHeight + navHeight + 12);
    document.documentElement.style.setProperty('--runcam-detail-scroll-offset', `${offset}px`);
    this.stickyContainer.style.setProperty('--runcam-detail-header-offset', `${Math.round(headerHeight)}px`);
    this.updateStickyState();
    this.scheduleActiveUpdate();
  }

  updateStickyState() {
    const stickyTop = parseFloat(getComputedStyle(this.stickyContainer).top) || 0;
    const isStuck = window.scrollY > 0 && this.stickyContainer.getBoundingClientRect().top <= stickyTop + 1;
    this.classList.toggle('is-stuck', isStuck);
  }

  handleScroll() {
    this.updateStickyState();
    if (this.pendingTargetId) {
      this.scheduleProgrammaticScrollEnd();
      return;
    }
    this.scheduleActiveUpdate();
  }

  handleScrollEnd() {
    if (!this.pendingTargetId) return;
    this.cancelProgrammaticScroll();
    this.scheduleActiveUpdate();
  }

  handleUserScrollIntent(event) {
    if (!this.pendingTargetId) return;
    const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
    if (event.type === 'keydown' && !scrollKeys.includes(event.key)) return;
    this.handleScrollEnd();
  }

  scheduleProgrammaticScrollEnd() {
    if (this.scrollEndTimer !== undefined) clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(this.handleScrollEnd, 200);
  }

  cancelProgrammaticScroll() {
    this.pendingTargetId = null;
    if (this.scrollEndTimer === undefined) return;
    clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = undefined;
  }

  scheduleActiveUpdate() {
    if (this.scrollFrame !== undefined) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = undefined;
      this.updateActiveState();
    });
  }

  updateActiveState() {
    if (this.pendingTargetId || !this.sections?.length) return;

    const offsetValue = getComputedStyle(document.documentElement).getPropertyValue('--runcam-detail-scroll-offset');
    const activationY = (parseInt(offsetValue, 10) || 120) + 1;
    const pageBottom = window.scrollY + window.innerHeight;
    const documentBottom = document.documentElement.scrollHeight;
    let activeSection = this.sections[0];

    if (pageBottom >= documentBottom - 2) {
      activeSection = this.sections[this.sections.length - 1];
    } else {
      for (const section of this.sections) {
        if (section.getBoundingClientRect().top > activationY) break;
        activeSection = section;
      }
    }

    this.setActive(activeSection.id);
  }

  handleClick(event) {
    const link = event.target.closest('[data-target-id]');
    if (!link || !this.contains(link)) return;
    const target = document.getElementById(link.dataset.targetId);
    if (!target) return;

    event.preventDefault();
    this.cancelProgrammaticScroll();
    this.pendingTargetId = target.id;
    this.setActive(target.id);
    this.scheduleProgrammaticScrollEnd();
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    history.replaceState(null, '', `#${target.id}`);
  }

  setActive(id) {
    if (!id || this.activeId === id) return;
    this.activeId = id;
    let activeLink = null;
    this.querySelectorAll('[data-target-id]').forEach((link) => {
      const active = link.dataset.targetId === id;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
      if (active) activeLink = link;
    });

    const list = this.querySelector('[data-runcam-detail-links]');
    if (!activeLink || !list) return;
    const linkRect = activeLink.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    const edgePadding = 4;
    let left = null;

    if (linkRect.left < listRect.left + edgePadding) {
      left = list.scrollLeft + linkRect.left - listRect.left - edgePadding;
    } else if (linkRect.right > listRect.right - edgePadding) {
      left = list.scrollLeft + linkRect.right - listRect.right + edgePadding;
    }

    if (left === null) return;
    list.scrollTo({
      left: Math.max(0, left),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('runcam-detail-navigation')) {
  customElements.define('runcam-detail-navigation', RuncamDetailNavigation);
}

class RuncamPurchaseLink extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a');
    this.handleClick = (event) => {
      const purchaseArea = document.querySelector('[data-testid="product-information"]');
      if (!purchaseArea) return;
      event.preventDefault();
      purchaseArea.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      purchaseArea.querySelector('button, input, select, a[href]')?.focus({ preventScroll: true });
    };
    this.link?.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.handleClick);
  }
}

if (!customElements.get('runcam-purchase-link')) {
  customElements.define('runcam-purchase-link', RuncamPurchaseLink);
}
})();
